import { useState } from "react";
import dayjs from "dayjs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock } from "lucide-react";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

export default function DateTimePicker({ value, onChange, testId, placeholder = "Pick date & time" }) {
  const [open, setOpen] = useState(false);
  const d = value ? dayjs(value) : null;

  const h12 = d ? d.hour() % 12 || 12 : 9;
  const minute = d ? Math.round(d.minute() / 5) * 5 % 60 : 0;
  const ampm = d ? (d.hour() < 12 ? "AM" : "PM") : "AM";

  const emit = (nd) => onChange(nd.format("YYYY-MM-DDTHH:mm"));

  const pickDate = (day) => {
    if (!day) return;
    const base = d || dayjs().hour(9).minute(0);
    emit(dayjs(day).hour(base.hour()).minute(base.minute()).second(0));
  };

  const setTime = (nh12, nm, nap) => {
    const h = (Number(nh12) % 12) + (nap === "PM" ? 12 : 0);
    const base = d || dayjs();
    emit(base.hour(h).minute(Number(nm)).second(0));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-white px-3 text-left text-sm ring-offset-background transition-colors hover:border-stark-terracotta focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <CalendarClock className="h-4 w-4 text-stark-muted" />
          <span className={d ? "text-stark-ink" : "text-stark-muted"}>
            {d ? d.format("MMM D, YYYY · h:mm A") : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={d ? d.toDate() : undefined} onSelect={pickDate} initialFocus />
        <div className="flex items-center gap-2 border-t border-stark-border p-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-stark-muted">Time</span>
          <Select value={String(h12)} onValueChange={(v) => setTime(v, minute, ampm)}>
            <SelectTrigger className="h-9 w-16" data-testid={testId ? `${testId}-hour` : undefined}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-56">{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
          </Select>
          <span className="font-bold text-stark-muted">:</span>
          <Select value={String(minute)} onValueChange={(v) => setTime(h12, v, ampm)}>
            <SelectTrigger className="h-9 w-16"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-56">{MINUTES.map((m) => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ampm} onValueChange={(v) => setTime(h12, minute, v)}>
            <SelectTrigger className="h-9 w-[68px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
}
